import { createClient } from '@supabase/supabase-js';
import type { Database } from '../types/database.types';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('Supabase credentials not found. Please set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY environment variables.');
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
  },
});

// Helper functions for common operations

export const supabaseAdmin = createClient<Database>(
  supabaseUrl,
  process.env.REACT_APP_SUPABASE_SERVICE_ROLE_KEY || supabaseAnonKey
);

// Speaking Events
export const getSpeakingEvents = async (includeUnpublished = false) => {
  let query = supabase
    .from('speaking_events')
    .select('*')
    .order('date_start', { ascending: false });

  if (!includeUnpublished) {
    query = query.eq('is_published', true);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data;
};

export const getSpeakingEvent = async (id: string) => {
  const { data, error } = await supabase
    .from('speaking_events')
    .select('*')
    .eq('id', id)
    .single();

  if (error) throw error;
  return data;
};

// Articles
export const getArticles = async (includeUnpublished = false) => {
  let query = supabase
    .from('articles')
    .select('*')
    .order('published_at', { ascending: false });

  if (!includeUnpublished) {
    query = query.eq('is_published', true);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data;
};

export const getArticle = async (slug: string) => {
  const { data, error } = await supabase
    .from('articles')
    .select('*')
    .eq('slug', slug)
    .single();

  if (error) throw error;
  
  // Increment view count
  if (data) {
    // @ts-ignore - Supabase type inference issue
    await supabase
      .from('articles')
      // @ts-ignore
      .update({ views_count: (data.views_count || 0) + 1 })
      // @ts-ignore
      .eq('id', data.id);
  }

  return data;
};

// Contact Submissions
export const submitContact = async (data: {
  full_name: string;
  email: string;
  message: string;
  submission_type?: string;
}) => {
  const { data: result, error } = await supabase
    .from('contact_submissions')
    .insert([data] as any)
    .select()
    .single();

  if (error) throw error;
  return result;
};

// Newsletter Subscriptions
export const subscribeNewsletter = async (email: string, full_name?: string) => {
  const { data, error } = await supabase
    .from('newsletter_subscribers')
    .insert([{ email, full_name }] as any)
    .select()
    .single();

  if (error) {
    // Check if already subscribed
    if (error.code === '23505') {
      throw new Error('This email is already subscribed to the newsletter.');
    }
    throw error;
  }
  return data;
};

export const unsubscribeNewsletter = async (token: string) => {
  const { data, error } = await supabase
    .from('newsletter_subscribers')
    // @ts-ignore - Supabase type inference issue
    .update({ is_active: false, unsubscribed_at: new Date().toISOString() })
    .eq('unsubscribe_token', token)
    .select()
    .single();

  if (error) throw error;
  return data;
};

// Admin Dashboard Stats
export const getDashboardStats = async () => {
  try {
    // Get total articles
    const { count: totalArticles } = await supabase
      .from('articles')
      .select('*', { count: 'exact', head: true });

    // Get published articles
    const { count: publishedArticles } = await supabase
      .from('articles')
      .select('*', { count: 'exact', head: true })
      .eq('is_published', true);

    // Get draft articles
    const { count: draftArticles } = await supabase
      .from('articles')
      .select('*', { count: 'exact', head: true })
      .eq('is_published', false);

    // Get total speaking events
    const { count: totalSpeakingEvents } = await supabase
      .from('speaking_events')
      .select('*', { count: 'exact', head: true });

    // Get upcoming speaking events (future dates)
    const { count: upcomingSpeakingEvents } = await supabase
      .from('speaking_events')
      .select('*', { count: 'exact', head: true })
      .gte('date_start', new Date().toISOString());

    // Get total newsletter subscribers
    const { count: totalNewsletterSubscribers } = await supabase
      .from('newsletter_subscribers')
      .select('*', { count: 'exact', head: true });

    // Get active newsletter subscribers
    const { count: activeNewsletterSubscribers } = await supabase
      .from('newsletter_subscribers')
      .select('*', { count: 'exact', head: true })
      .eq('is_active', true);

    // Get total contact submissions
    const { count: totalContactSubmissions } = await supabase
      .from('contact_submissions')
      .select('*', { count: 'exact', head: true });

    // Get unread contact submissions
    const { count: unreadContactSubmissions } = await supabase
      .from('contact_submissions')
      .select('*', { count: 'exact', head: true })
      .is('is_read', false);

    // Get total article views
    const { data: articlesData } = await supabase
      .from('articles')
      .select('views_count');
    
    // @ts-ignore - Type inference issue with views_count
    const totalArticleViews = articlesData?.reduce((sum, article) => sum + (article.views_count || 0), 0) || 0;

    return {
      total_articles: totalArticles || 0,
      published_articles: publishedArticles || 0,
      draft_articles: draftArticles || 0,
      total_speaking_events: totalSpeakingEvents || 0,
      upcoming_speaking_events: upcomingSpeakingEvents || 0,
      total_newsletter_subscribers: totalNewsletterSubscribers || 0,
      active_newsletter_subscribers: activeNewsletterSubscribers || 0,
      total_contact_submissions: totalContactSubmissions || 0,
      unread_contact_submissions: unreadContactSubmissions || 0,
      total_article_views: totalArticleViews,
    };
  } catch (error) {
    console.error('Error fetching dashboard stats:', error);
    throw error;
  }
};

// Email Logs
export const logEmail = async (data: {
  recipient_email: string;
  email_type: string;
  subject: string;
  status?: string;
  error_message?: string;
}) => {
  const { error } = await supabase
    .from('email_logs')
    .insert([data] as any);

  if (error) console.error('Failed to log email:', error);
};

// Content Generation Logs
export const logContentGeneration = async (data: {
  article_id?: string;
  prompt: string;
  tokens_used?: number;
  generation_time?: number;
  status?: string;
  error_message?: string;
}) => {
  const { error } = await supabase
    .from('content_generation_logs')
    .insert([data] as any);

  if (error) console.error('Failed to log content generation:', error);
};

// Auth functions
export const signIn = async (email: string, password: string) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  return { data, error };
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  return { error };
};

export const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

export const isAdmin = async (): Promise<boolean> => {
  const user = await getCurrentUser();
  if (!user || !user.email) return false;
  
  // @ts-ignore - Supabase type inference issue
  const { data, error } = await supabase
    .from('admin_users')
    .select('id, role')
    // @ts-ignore
    .eq('email', user.email)
    .single();
  
  // User exists in admin_users table and has admin role
  // @ts-ignore - Type inference issue with data.role
  return !error && !!data && data.role === 'admin';
};

// Admin CRUD operations for Speaking Events
export const createSpeakingEvent = async (data: any) => {
  // @ts-ignore - Supabase type inference issue
  const { data: result, error } = await supabase
    .from('speaking_events')
    // @ts-ignore
    .insert([data])
    .select()
    .single();
  
  if (error) throw error;
  return result;
};

export const updateSpeakingEvent = async (id: string, data: any) => {
  // @ts-ignore - Supabase type inference issue
  const { data: result, error } = await supabase
    .from('speaking_events')
    // @ts-ignore
    .update(data)
    .eq('id', id)
    .select()
    .single();
  
  if (error) throw error;
  return result;
};

export const deleteSpeakingEvent = async (id: string) => {
  const { error } = await supabase
    .from('speaking_events')
    .delete()
    .eq('id', id);
  
  if (error) throw error;
};

// Admin CRUD operations for Articles
export const createArticle = async (data: any) => {
  // @ts-ignore - Supabase type inference issue
  const { data: result, error } = await supabase
    .from('articles')
    // @ts-ignore
    .insert([data])
    .select()
    .single();
  
  if (error) throw error;
  return result;
};

export const updateArticle = async (id: string, data: any) => {
  // @ts-ignore - Supabase type inference issue
  const { data: result, error } = await supabase
    .from('articles')
    // @ts-ignore
    .update(data)
    .eq('id', id)
    .select()
    .single();
  
  if (error) throw error;
  return result;
};

export const deleteArticle = async (id: string) => {
  const { error } = await supabase
    .from('articles')
    .delete()
    .eq('id', id);
  
  if (error) throw error;
};

// Get contact submissions for admin
export const getContactSubmissions = async () => {
  const { data, error } = await supabase
    .from('contact_submissions')
    .select('*')
    .order('created_at', { ascending: false });
  
  if (error) throw error;
  return data;
};

// Get newsletter subscribers for admin
export const getNewsletterSubscribers = async () => {
  const { data, error } = await supabase
    .from('newsletter_subscribers')
    .select('*')
    .order('subscribed_at', { ascending: false });
  
  if (error) throw error;
  return data;
};

// Mark contact submission as read
export const markContactAsRead = async (id: string) => {
  const { error } = await supabase
    .from('contact_submissions')
    // @ts-ignore
    .update({ is_read: true })
    .eq('id', id);
  
  if (error) throw error;
};